/**
 * Utility functions for Maintenance component
 */

import { getWorkOrderDisplayNumber } from '../../../utils/workOrderDisplayNumber';

export const WO_DETAIL_TABS = {
  OVERVIEW: 'overview',
  CAMO_QM_REVIEW: 'camoQmReview',
  WORKFLOW: 'workflow',
  LINKED: 'linked',
  AMP_CHECKS: 'ampChecks',
  TOOLS: 'tools',
  SPARES: 'spares',
  CONSUMABLES: 'consumables',
  VERIFICATION: 'verification',
  ACTIONS: 'actions'
};

/**
 * Get Material-UI color for status chip
 */
export const getStatusColor = (status) => {
  switch (status) {
    case 'SCHEDULED': return 'warning';
    case 'HOLD': return 'default';
    case 'IN_PROGRESS': return 'info';
    case 'PARTS_PROCURED': return 'info';
    case 'COMPLETED': return 'success';
    case 'CANCELLED': return 'error';
    default: return 'default';
  }
};

/**
 * Get Material-UI color for priority chip
 */
export const getPriorityColor = (priority) => {
  switch (priority) {
    case 'LOW': return 'success';
    case 'MEDIUM': return 'warning';
    case 'HIGH': return 'error';
    case 'CRITICAL': return 'error';
    default: return 'default';
  }
};

/**
 * Calculate days until due date
 */
export const getDaysUntilDue = (dueDate) => {
  if (!dueDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffTime = due - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Get urgency color based on days until due
 */
export const getUrgencyColor = (daysUntilDue) => {
  if (daysUntilDue === null) return 'default';
  if (daysUntilDue < 0) return 'error'; // Overdue
  if (daysUntilDue <= 7) return 'error'; // Critical
  if (daysUntilDue <= 30) return 'warning'; // Urgent
  if (daysUntilDue <= 90) return 'info'; // Soon
  return 'success'; // Future
};

/**
 * Filter maintenance items based on search and filters
 */
export const filterMaintenance = (maintenance, searchTerm, statusFilter, priorityFilter, typeFilter, aircraftFilter = 'ALL') => {
  return maintenance.filter((item) => {
    // Search filter (work order number, title, aircraft registration)
    const searchLower = searchTerm.toLowerCase();
    const woNum = getWorkOrderDisplayNumber(item);
    const matchesSearch = !searchTerm || 
      woNum.toString().toLowerCase().includes(searchLower) ||
      item.workOrderNumber?.toString().toLowerCase().includes(searchLower) ||
      item.physicalWorkOrderNumber?.toString().toLowerCase().includes(searchLower) ||
      item.title?.toLowerCase().includes(searchLower) ||
      item.aircraftId?.registration?.toLowerCase().includes(searchLower);
    
    // Status filter
    const matchesStatus = statusFilter === 'ALL' || item.status === statusFilter;
    
    // Priority filter
    const matchesPriority = priorityFilter === 'ALL' || item.priority === priorityFilter;
    
    // Type filter
    const matchesType = typeFilter === 'ALL' || item.type === typeFilter;
    
    // Aircraft filter (by aircraft id)
    const aircraftId = item.aircraftId?._id || item.aircraftId;
    const matchesAircraft = aircraftFilter === 'ALL' || (aircraftId && aircraftId.toString() === aircraftFilter);
    
    return matchesSearch && matchesStatus && matchesPriority && matchesType && matchesAircraft;
  });
};

/** AMP schedule or upcoming row is one-time (no recurring interval). */
export const isOneTimeAmpSchedule = (scheduleOrItem) =>
  !scheduleOrItem?.recurrenceType ||
  scheduleOrItem.recurrenceType === 'ONE_TIME' ||
  scheduleOrItem.inspectionCategory === 'ONE_TIME_INSPECTIONS';

/** Assembly marked NA (Not Applicable) on an AMP schedule / upcoming AMP row. */
export const isAmpAssemblyNotApplicable = (item) => {
  if (item?.source !== 'AMP_SCHEDULE') return false;
  if (Boolean(item.isNA)) return true;
  if (item.ampAssemblyLabel === 'NA') return true;
  const rem = item.ampRemainingLines;
  if (
    Array.isArray(rem) &&
    rem.some((line) => (line?.tooltip || '').includes('Not applicable (assembly N/A)'))
  ) {
    return true;
  }
  return false;
};

export const SCHEDULE_FILTER_LABELS = {
  ALL: 'All',
  NOT_APPLICABLE: 'Not Applicable',
  EXCLUDE_NOT_APPLICABLE: 'Exclude Not Applicable',
  ONE_TIME: 'One time',
  EXCLUDE_ONE_TIME: 'Exclude one time'
};

export const UPCOMING_SOURCE_FILTER_LABELS = {
  ALL: 'All Sources',
  COMPONENT_INSPECTION: 'Inspection',
  COMPONENT_SERVICE: 'Service',
  ASSEMBLY_TIME_SINCE: 'Assembly TSR',
  COMPONENT_TIME_SINCE: 'Component TSR',
  AMP_SCHEDULE: 'AMP Schedule',
  ADSB_SERVICE_BULLETIN: 'AD/SB'
};

/** @param {'ALL'|keyof typeof UPCOMING_SOURCE_FILTER_LABELS} filter */
export const matchesUpcomingSourceFilter = (item, filter) => {
  if (filter === 'ALL') return true;
  return item?.source === filter;
};

export const BULLETIN_FILTER_LABELS = {
  ALL: 'All',
  NOT_APPLICABLE: 'Not Applicable',
  EXCLUDE_NOT_APPLICABLE: 'Exclude Not Applicable',
  ONE_TIME: 'One time',
  EXCLUDE_ONE_TIME: 'Exclude one time',
  INCLUDE_NOT_APPLICABLE_AND_ONE_TIME: 'Include Not Applicable and One time',
  EXCLUDE_NOT_APPLICABLE_AND_ONE_TIME: 'Exclude Not Applicable and One time'
};

/** Bulletin-level Not Applicable on an upcoming AD/SB row. */
export const isAdsbBulletinNotApplicable = (item) => {
  if (item?.source !== 'ADSB_SERVICE_BULLETIN') return false;
  return item.bulletinApplicable === false;
};

/**
 * Filter upcoming AD/SB rows by bulletin NA / one-time.
 * @param {keyof typeof BULLETIN_FILTER_LABELS} filter
 */
export const matchesAdsbBulletinTypeFilter = (item, filter) => {
  if (filter === 'ALL') return true;
  if (item?.source !== 'ADSB_SERVICE_BULLETIN') return true;
  const isNA = isAdsbBulletinNotApplicable(item);
  const isOneTime = item.recurrenceType === 'ONE_TIME';
  if (filter === 'NOT_APPLICABLE') return isNA;
  if (filter === 'EXCLUDE_NOT_APPLICABLE') return !isNA;
  if (filter === 'ONE_TIME') return isOneTime;
  if (filter === 'EXCLUDE_ONE_TIME') return !isOneTime;
  if (filter === 'INCLUDE_NOT_APPLICABLE_AND_ONE_TIME') return isNA || isOneTime;
  if (filter === 'EXCLUDE_NOT_APPLICABLE_AND_ONE_TIME') return !isNA && !isOneTime;
  return true;
};

/** One-time AMP schedule or AD/SB bulletin on an upcoming row. */
export const isOneTimeUpcomingItem = (item) => {
  if (item?.source === 'ADSB_SERVICE_BULLETIN') {
    return item.recurrenceType === 'ONE_TIME';
  }
  if (item?.source === 'AMP_SCHEDULE') {
    return isOneTimeAmpSchedule(item);
  }
  return false;
};

/**
 * Filter upcoming rows by assembly NA / one-time (AMP + AD/SB for one-time filters).
 * @param {'ALL'|'NOT_APPLICABLE'|'EXCLUDE_NOT_APPLICABLE'|'ONE_TIME'|'EXCLUDE_ONE_TIME'} filter
 */
export const matchesAmpScheduleFilter = (item, filter) => {
  if (filter === 'ALL') return true;
  const isAmp = item?.source === 'AMP_SCHEDULE';
  const isNA = isAmp && isAmpAssemblyNotApplicable(item);
  const isOneTime = isOneTimeUpcomingItem(item);
  if (filter === 'NOT_APPLICABLE') return isNA;
  if (filter === 'EXCLUDE_NOT_APPLICABLE') return isAmp ? !isNA : true;
  if (filter === 'ONE_TIME') {
    return isOneTime;
  }
  if (filter === 'EXCLUDE_ONE_TIME') {
    if (item?.source === 'AMP_SCHEDULE' || item?.source === 'ADSB_SERVICE_BULLETIN') {
      return !isOneTime;
    }
    return true;
  }
  return true;
};

/**
 * Filter upcoming maintenance items
 */
export const filterUpcomingMaintenance = (upcomingMaintenance, searchTerm) => {
  return upcomingMaintenance.filter((item) => {
    const matchesSearch = !searchTerm || 
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.aircraftRegistration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.componentCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.componentDescription?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });
};
